/**
 * ElasticSearch API Client
 *
 * HTTP client for making search requests to ElasticSearch.
 * Supports single search and multi-search operations.
 *
 * Uses native fetch which is polyfilled by Next.js for Node.js environments.
 * Pattern follows olx-motors-nextjs API routes but adapted for isomorphic use.
 */

import { ES_CONFIG } from "./config"

/**
 * Base64 encode a string (works in both browser and Node.js)
 * @param {string} str - String to encode
 * @returns {string} Base64 encoded string
 */
const base64Encode = (str) => {
  if (typeof btoa !== "undefined") {
    return btoa(str)
  }
  return Buffer.from(str).toString("base64")
}

/**
 * Response filter paths to minimize response payload size
 */
const RESPONSE_FILTER_PATHS = [
  "took",
  "*.took",
  "*.timed_out",
  "*.suggest.*.options.text",
  "*.suggest.*.options._source.*",
  "*.hits.total.*",
  "*.hits.hits._source.*",
  "*.hits.hits._score",
  "*.hits.hits.highlight.*",
  "*.error",
  "*.aggregations.*.buckets.key",
  "*.aggregations.*.buckets.doc_count",
  "*.aggregations.*.buckets.complex_value.hits.hits._source",
  "*.aggregations.*.filtered_agg.facet.buckets.key",
  "*.aggregations.*.filtered_agg.facet.buckets.doc_count",
]

/**
 * ElasticSearch API Client Class
 */
class ElasticAPIClient {
  constructor(options = {}) {
    this.url = options.url || ES_CONFIG.url
    this.credentials = options.credentials || ES_CONFIG.credentials
    this.timeout = options.timeout || ES_CONFIG.timeout
  }

  /**
   * Build authorization headers
   * @returns {Object} Headers object with authorization if credentials exist
   */
  buildHeaders() {
    const headers = {
      "Content-Type": "application/json; charset=utf-8",
    }

    if (this.credentials) {
      const cleanCredentials = this.credentials.trim()
      const encodedCredentials = base64Encode(cleanCredentials)
      headers["Authorization"] = `Basic ${encodedCredentials}`
    }

    return headers
  }

  /**
   * Create full request URL
   * @param {string} path - API path
   * @returns {string} Full URL
   */
  createRequestURL(path) {
    if (!this.url) {
      throw new Error("ElasticSearch URL not configured. Set ELASTIC_SEARCH_API_URL environment variable.")
    }
    const cleanedPath = path.startsWith("/") ? path.slice(1) : path
    const baseUrl = this.url.endsWith("/") ? this.url : `${this.url}/`
    return `${baseUrl}${cleanedPath}`
  }

  /**
   * Create filter path query param
   * @returns {string} Encoded filter path
   */
  createFilterPath() {
    return encodeURIComponent(RESPONSE_FILTER_PATHS.join(","))
  }

  /**
   * Perform a single search request
   * @param {string} indexName - Index to search
   * @param {Object} request - Search request body
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Search response
   */
  async search(indexName, request, options = {}) {
    const { useFilterPath = true, timeoutSeconds = 30 } = options

    // Add timeout to request body (ES handles timeout server-side)
    const requestBody = { ...request, timeout: `${timeoutSeconds}s` }

    const filterPath = useFilterPath ? `?filter_path=${this.createFilterPath()}` : ""
    const path = `/${indexName}/_search${filterPath}`
    const url = this.createRequestURL(path)

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.buildHeaders(),
        body: JSON.stringify(requestBody),
      })

      const responseBody = await response.json()

      if (responseBody.error) {
        throw new Error(`ElasticSearch search error: ${responseBody.error.reason || JSON.stringify(responseBody.error)}`)
      }

      if (responseBody.timed_out) {
        throw new Error("ElasticSearch search timed out")
      }

      return responseBody
    } catch (error) {
      throw error
    }
  }

  /**
   * Perform multiple search requests in a single call
   * @param {Array<{indexName: string, request: Object}>} requests - Array of search requests
   * @param {Object} options - Additional options
   * @returns {Promise<Array<Object>>} Array of search responses
   */
  async multiSearch(requests, options = {}) {
    const { timeoutSeconds = 30 } = options

    // Build NDJSON body
    let body = ""
    requests.forEach(({ indexName, request }) => {
      // Add timeout to each request body
      const requestBody = { ...request, timeout: `${timeoutSeconds}s` }

      body += `${JSON.stringify({ index: indexName })}\n`
      body += `${JSON.stringify(requestBody)}\n`
    })

    const filterPath = this.createFilterPath()
    const path = `/_msearch?filter_path=${filterPath}`
    const url = this.createRequestURL(path)

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          ...this.buildHeaders(),
          "Content-Type": "application/x-ndjson",
        },
        body,
      })

      const responseBody = await response.json()

      if (responseBody.error) {
        throw new Error(
          `ElasticSearch multi-search error: ${responseBody.error.reason || JSON.stringify(responseBody.error)}`
        )
      }

      const responses = responseBody.responses || []

      // Check for errors in individual responses
      const responsesWithErrors = responses.filter((r) => r.error)
      if (responsesWithErrors.length) {
        throw new Error(
          `ElasticSearch multi-search had errors: ${JSON.stringify(responsesWithErrors.map((r) => r.error))}`
        )
      }

      // Check for timeouts in individual responses
      const responsesTimedOut = responses.filter((r) => r.timed_out)
      if (responsesTimedOut.length) {
        throw new Error("One or more ElasticSearch searches timed out")
      }

      return responses
    } catch (error) {
      throw error
    }
  }

  /**
   * Get document count for a query
   * @param {string} indexName - Index to count
   * @param {Object} request - Query request body
   * @returns {Promise<number>} Document count
   */
  async count(indexName, request) {
    // _count API doesn't support these fields
    const { sort, size, from, _source, track_total_hits, ...queryBody } = request

    const path = `/${indexName}/_count`
    const url = this.createRequestURL(path)

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.buildHeaders(),
        body: JSON.stringify(queryBody),
      })

      const responseBody = await response.json()

      if (responseBody.error) {
        throw new Error(`ElasticSearch count error: ${responseBody.error.reason || JSON.stringify(responseBody.error)}`)
      }

      return responseBody.count
    } catch (error) {
      throw error
    }
  }
}

/**
 * Create a new ElasticSearch API client instance
 * @param {Object} options - Client options
 * @returns {ElasticAPIClient} Client instance
 */
export const createElasticClient = (options = {}) => {
  return new ElasticAPIClient(options)
}

export default ElasticAPIClient
